import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@inertiajs/react';
import { cn } from '../../lib/cn';
import FrequencyBadge from './FrequencyBadge';
import PosBadge from './PosBadge';

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function Spinner({ className }) {
  return (
    <svg className={cn("animate-spin", className)} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

export default function WordSearch({
  value = '',
  onChange,
  onSearch,
  results = [],
  isLoading = false,
  placeholder: placeholderProp,
  inputTestId,
  className
}) {
  const { t } = useTranslation();
  const placeholder = placeholderProp || t('dictionary.searchPlaceholder');
  const [isFocused, setIsFocused] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const handleChange = useCallback((e) => {
    const query = e.target.value;
    if (onChange) onChange(query);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (onSearch) onSearch(query);
    }, 300);
  }, [onChange, onSearch]);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const showDropdown = isFocused && (results.length > 0 || isLoading);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          placeholder={placeholder}
          data-testid={inputTestId}
          className={cn(
            "w-full pl-12 pr-4 py-4 text-lg rounded-xl",
            "bg-white dark:bg-gray-800",
            "border border-gray-200 dark:border-gray-700",
            "text-gray-900 dark:text-white",
            "placeholder-gray-400 dark:placeholder-gray-500",
            "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
            "transition-shadow"
          )}
        />
        {isLoading && (
          <Spinner className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">
          {isLoading && results.length === 0 && (
            <div className="px-4 py-3 text-gray-500 dark:text-gray-400 text-center">
              {t('dictionary.searching')}
            </div>
          )}
          {results.map((word) => (
            <Link
              key={word.id}
              href={`/teacher/dictionary/${word.id}`}
              className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {word.lemma}
                  </span>
                  <PosBadge pos={word.pos} size="sm" />
                </div>
                <FrequencyBadge band={word.frequencyBand} size="sm" />
              </div>
              {word.primaryDefinition && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
                  {word.primaryDefinition}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
